import React, { useMemo } from 'react'
import {
  typeList,
  comingSoon,
  GROUP_MENU,
} from 'app/main/composeMessage/messageType/useMessageType'
import { FormattedMessage } from 'react-intl'
import { makeStyles } from '@material-ui/styles'
import { useField } from 'formik'
import PropTypes from 'prop-types'
import { useFlag } from '@unleash/proxy-client-react'
import {
  MenuItem,
  FormControl,
  Select,
  InputLabel,
  Divider,
  Typography,
  Input,
} from '@material-ui/core'
import clsx from 'clsx'
import * as R from 'ramda'
import { TYPE_GENERAL, TYPE_EVENT, TYPE_POLL } from 'app/constants/messageType'
// Styling using makeStyles for custom CSS
const useStyles = makeStyles((theme) => ({
  root: {
    backgroundColor: theme.palette.disable.default,
  },
  subMenu: {
    paddingLeft: '35px',
  },
  headMenu: {
    '&.MuiListSubheader-sticky': {
      position: 'unset',
    },
  },
  titleGroup: {
    '&.MuiListItem-root.Mui-disabled': {
      opacity: 1,
    },
  },
  inputBorderError: {
    '& .MuiOutlinedInput-input': {
      border: '1px solid red',
      borderRadius: '4px',
    },
  },
}))

const getMessageTypeList = (
  isDraft,
  isSchedule,
  isBeacon,
  isPoll,
  isEditMode,
  mes_5281_save_draft_for_polls,
) => {
  if (isBeacon) return [TYPE_GENERAL]

  const types = [TYPE_GENERAL, TYPE_EVENT]

  // feature flag enables message type change to poll
  // for now only enable in draft mode but not in edit scheduled message mode
  const shouldExcludePoll = mes_5281_save_draft_for_polls
    ? isEditMode && isSchedule
    : isDraft || (isSchedule && !isPoll)

  return shouldExcludePoll ? types : [...types, TYPE_POLL]
}

const MessageTypeSelector = (props) => {
  const [field, meta, helpers] = useField(props.name)
  const { value } = meta
  const { setValue } = helpers
  const classes = useStyles()
  const isBeacon = props.isBeacon
  const isDraft = props.isDraft
  const isSchedule = props.isSchedule
  const isPoll = props.isPoll
  const isEditMode = props.isEditMode
  const mes_5281_save_draft_for_polls = useFlag('mes_5281_save_draft_for_polls')

  const allowedTypes = getMessageTypeList(
    isDraft,
    isSchedule,
    isBeacon,
    isPoll,
    isEditMode,
    mes_5281_save_draft_for_polls,
  )

  // Memoized rendering of the menu items to avoid recalculating on each render
  const menuItems = useMemo(() => {
    return typeList
      .filter((item) => R.includes(item.id, allowedTypes))
      .map((item) => {
        if (item.type === GROUP_MENU) {
          return [
            <MenuItem
              key={item.id}
              disabled
              className={clsx(classes.titleGroup, 'text-blue-950')}
            >
              {item.display}
            </MenuItem>,
            ...item.subMenu.map((menu) => (
              <MenuItem
                className={clsx(classes.subMenu, 'text-blue-950')}
                disabled={menu.disabled}
                key={menu.id}
                value={menu.id}
              >
                {menu.display}
              </MenuItem>
            )),
          ]
        }
        return (
          <MenuItem key={item.id} value={item.id} className="text-blue-950">
            {item.display}
          </MenuItem>
        )
      })
  }, [classes, allowedTypes])

  // Memoized coming soon items
  const comingSoonItems = useMemo(() => {
    return comingSoon.map((item) => (
      <MenuItem
        key={item.id}
        value={item.id}
        disabled
        className="text-blue-950"
      >
        {item.display}
      </MenuItem>
    ))
  }, [])

  return (
    <FormControl
      className={clsx(
        props.className,
        props.disabled && 'cursor-not-allowed',
        'w-full',
      )}
      variant={props.variant}
      required={props.required}
      disabled={props.disabled}
      error={meta.touched && !!meta.error} // Handling error state
    >
      {props.label && (
        <InputLabel htmlFor={props.name}>{props.label}</InputLabel>
      )}

      <Select
        {...field}
        value={value}
        disableUnderline
        input={
          <Input
            id={props.name}
            className={clsx(
              'p-14 h-48 rounded-8 border border-grey-input text-blue-950',
              props.disabled && 'cursor-not-allowed opacity-75 text-grey-200',
              meta.touched && meta.error && classes.inputBorderError, // Applying error class
            )}
            aria-label={props.label} // Improving accessibility
          />
        }
        onChange={(event) => {
          setValue(event.target.value)
          if (props.onChange) {
            props.onChange(event.target.value)
          }
        }}
      >
        <MenuItem disabled value="" className="text-blue-950">
          <FormattedMessage id="createNotification.dialog.selector.type" />
        </MenuItem>
        {menuItems}
        <MenuItem disabled className="text-blue-950">
          <div className="flex w-full content-center items-center">
            <Divider
              className={clsx(classes.root, 'flex-1 lg:w-3/4')}
              variant="middle"
            />
            <Typography className="flex flex-none text-center">
              <FormattedMessage id="notiType.comingsoon.label" />
            </Typography>
            <Divider
              className={clsx(classes.root, 'flex-1 lg:w-3/4')}
              variant="middle"
            />
          </div>
        </MenuItem>
        {comingSoonItems}
      </Select>

      {/* Display error message if validation error exists */}
      {meta.touched && meta.error && (
        <Typography color="error" variant="caption" className="mt-1">
          {meta.error}
        </Typography>
      )}
    </FormControl>
  )
}

// PropTypes for validation
MessageTypeSelector.propTypes = {
  name: PropTypes.string.isRequired, // Formik field name
  label: PropTypes.string, // Optional label
  className: PropTypes.string, // Custom class name for styling
  variant: PropTypes.string, // Material-UI variant
  required: PropTypes.bool, // Whether the field is required
  disabled: PropTypes.bool, // Whether the selector is disabled
  isBeacon: PropTypes.bool,
  isEditMode: PropTypes.bool, // Whether the dialog is in edit mode for a retrieved message
  onChange: PropTypes.func, // Function to handle value change
}

export default MessageTypeSelector
